// ============================================================================
//  FeaturedBadge — "🔥 Featured on the live wall" prestige badge.
//
//  Per the user's brief (May 2026):
//    "Right now: guests create spins, spins appear on projector,
//     crowd reacts. But the guest themselves still doesn't get the
//     'WE made it.' moment directly."
//
//  This badge IS that moment. Gold glow, cinematic shimmer, subtle
//  pulse. Prestigious — NOT gamified. Never shows ranks, points, or
//  leaderboard positions; only the curated win itself.
//
//  Renders either:
//    • Category-specific ("🔥 Trending Tonight", "😂 Biggest Reaction"…)
//      when the booth wins exactly one category — the proudest framing
//    • Generic ("🔥 Featured on the live wall") + small subtitle when
//      the booth wins multiple categories (rare; keep the headline
//      reading as ONE prestigious moment, not a stack of accolades)
//
//  Props:
//    categories — array of { category_slug, category_label,
//                            category_icon, metric_value, metric_label }
//                 from /api/booth/360/{booth_id}/feature-status
//    compact    — when true, render inline (no big card chrome).
//                 Used inside the playback player overlay.
// ============================================================================
import React from 'react';
import { motion } from 'framer-motion';

const CATEGORY_ACCENT = {
  trending:         { color: '#FF6B6B', glow: 'rgba(255,107,107,0.55)' },
  crowd_favourite:  { color: '#D4AF5E', glow: 'rgba(212,175,94,0.55)'  },
  most_replayed:    { color: '#B89762', glow: 'rgba(184,151,98,0.55)'  },
  biggest_reaction: { color: '#FFB84D', glow: 'rgba(255,184,77,0.55)'  },
};
const DEFAULT_ACCENT = { color: '#D4AF5E', glow: 'rgba(212,175,94,0.55)' };

export const FeaturedBadge = ({ categories, compact = false }) => {
  if (!categories || categories.length === 0) return null;

  // Multi-win → generic headline + the first category as subtitle.
  // Single-win → use that category's icon + label as the headline so
  // the badge feels personal ("Biggest Reaction" lands harder than
  // "Featured on the live wall" when the guest knows WHY).
  const primary = categories[0];
  const accent = CATEGORY_ACCENT[primary.category_slug] || DEFAULT_ACCENT;
  const isMulti = categories.length > 1;
  const headline = isMulti
    ? '🔥 Featured on the live wall'
    : `${primary.category_icon || '🔥'} ${primary.category_label}`;
  const subtitle = isMulti
    ? `${primary.category_icon || '🔥'} ${primary.category_label} · +${categories.length - 1} more`
    : 'Everyone can see it live right now';

  const card = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: compact ? '10px 18px' : '14px 24px',
    borderRadius: 999,
    background: `linear-gradient(135deg, ${accent.color}24 0%, rgba(212,175,94,0.12) 100%)`,
    border: `1px solid ${accent.color}99`,
    boxShadow: `0 0 28px ${accent.glow}, inset 0 0 0 1px ${accent.color}30`,
    backdropFilter: 'blur(12px)',
  };

  return (
    <motion.div
      data-testid="featured-badge"
      data-featured-categories={categories.map((c) => c.category_slug).join(',')}
      initial={{ opacity: 0, y: 10, scale: 0.92 }}
      animate={{
        opacity: 1, y: 0, scale: 1,
        boxShadow: [
          `0 0 22px ${accent.glow}`,
          `0 0 44px ${accent.glow}`,
          `0 0 22px ${accent.glow}`,
        ],
      }}
      transition={{
        opacity: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
        y: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
        scale: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
        boxShadow: { duration: 2.8, ease: 'easeInOut', repeat: Infinity },
      }}
      style={card}
    >
      <span
        data-testid="featured-badge-headline"
        style={{
          fontSize: compact ? 13 : 14,
          fontWeight: 800,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: accent.color,
          textShadow: `0 0 14px ${accent.glow}`,
          whiteSpace: 'nowrap',
        }}
      >
        {headline}
      </span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.78)',
          letterSpacing: '0.04em',
          textAlign: 'center',
        }}
      >
        {subtitle}
      </span>
    </motion.div>
  );
};

export default FeaturedBadge;
